import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Base from '../Base';
import Icon from '../../components/common/Icon';
import { fetchUserInfo, teamPhoto } from '../../utils';

import '../user/PublicProfile.css';
import '../event/EventList.css';
import './Cheers.css';

const userFallback = require('./../../assets/images/fallback/user.png');

const RECENT_VISIBLE = 5;

const Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [cheersData, setCheersData] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [, setLoading] = useState(true);
  const [, setError] = useState(null);

  useEffect(() => {
    // The "Share your team" page is always about the user's own team.
    // own_team=1 makes the API ignore any stale Otherteamid header and
    // pull team_id from the JWT instead.
    fetchUserInfo('/team?own_team=1', setTeam, setLoading, setError);
    fetchUserInfo('/cheers?own_team=1', setCheersData, setLoading, setError);
  }, []);

  const cheers = cheersData?.cheers || [];
  const totalVotes = team?.cheer_count ?? cheers.length;
  const teamId = team?.id;
  const teamName = team?.name;
  const eventName = team?.event_name;
  const teamPicture = teamPhoto(team?.picture, team?.company_picture);
  const heroIsCompanyLogo = !team?.picture && Boolean(team?.company_picture);

  const recentCheers = useMemo(() => cheers.slice(0, RECENT_VISIBLE), [cheers]);
  const hasMoreCheers = cheers.length > RECENT_VISIBLE;

  const votingActive = team?.cheers_start_time && team?.cheers_end_time
    && new Date(team.cheers_start_time) < new Date()
    && new Date() < new Date(team.cheers_end_time);

  // Share straight to the public cheer form (first name / last name / email +
  // a message), not the base team page — anonymous visitors can cheer there
  // without an account or a login wall.
  const publicVoteUrl = teamId ? `${window.location.origin}/public-team/${teamId}/vote` : null;
  // Sharing is only useful once the team has its OWN photo (a company-logo
  // fallback doesn't count) — a photo-less vote page barely converts.
  const hasTeamPhoto = Boolean(team?.picture);

  const handleShare = async () => {
    if (!publicVoteUrl) return;
    const shareText = t('cheerMyTeam', { team: teamName, event: eventName });
    const sharePayload = {
      title: t('voteForTeam', { team: teamName }),
      text:  shareText,
      url:   publicVoteUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(sharePayload);
        return;
      } catch (e) {
        // user cancelled or share failed — fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(`${shareText}\n${publicVoteUrl}`);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch {
      window.prompt(t('share'), publicVoteUrl);
    }
  };

  return (
    <Base activeNav="home">
      <div className="pp-page">
        <div className="el-topbar">
          <button type="button" className="el-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <Icon name="angle-left" />
          </button>
          <h1 className="el-page-title">{t('cheers')}</h1>
        </div>

        {/* HERO — total votes */}
        <section className="ch-hero">
          <div className="ch-hero-bg" style={{ backgroundImage: `url(${teamPicture})` }} />
          <div className="ch-hero-inner">
            <div className="pp-avatar-ring">
              <img
                src={teamPicture}
                alt=""
                className={`pp-avatar ${heroIsCompanyLogo ? 'ch-avatar-contain' : ''}`}
              />
            </div>
            <div className="ch-hero-eyebrow">{t('cheers')}</div>
            <div className="ch-hero-count">{totalVotes}</div>
            {teamName && <div className="ch-hero-team">{teamName}</div>}
          </div>
        </section>

        {/* SHARE CARD */}
        <div className="ch-share-card">
          <div className="ch-share-icon">
            <Icon name={hasTeamPhoto ? 'megaphone' : 'picture'} theme="light" />
          </div>
          {hasTeamPhoto ? (
            <>
              <h2 className="ch-share-title">{t('shareYourTeam')}</h2>
              <p className="ch-share-sub">{t('shareYourTeamIntro')}</p>
              <button
                type="button"
                className={`ch-share-btn ${shareCopied ? 'ch-share-btn--copied' : ''}`}
                onClick={handleShare}
              >
                <Icon name={shareCopied ? 'check' : 'redo'} pack="sr" theme="light" />
                {shareCopied ? (t('copied') || 'Copied!') : t('share')}
              </button>
            </>
          ) : (
            <>
              <h2 className="ch-share-title">{t('cheerNeedPhotoTitle')}</h2>
              <p className="ch-share-sub">{t('cheerNeedPhotoBody')}</p>
              <Link to="/edit-team" className="ch-share-btn">
                <Icon name="picture" pack="sr" theme="light" />
                {t('addTeamPhoto')}
              </Link>
            </>
          )}
        </div>

        {/* CHEERS LEADERBOARD LINK */}
        <Link to="/cheers-leaderboard" className="ch-leaderboard-link">
          <span className="ch-leaderboard-icon">
            <Icon name="trophy" />
          </span>
          <div className="ch-leaderboard-meta">
            <p className="ch-leaderboard-title">{t('cheersLeaderboard')}</p>
            <p className="ch-leaderboard-sub">{t('seeWhereYouStand', { defaultValue: 'See where you stand' })}</p>
          </div>
          <Icon name="angle-right" className="ch-leaderboard-chevron" />
        </Link>

        {/* RECENT CHEERS */}
        {recentCheers.length > 0 ? (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('recentVotes') || t('cheers')}</h2>
              {hasMoreCheers && (
                <Link to="/all-cheers" className="ch-see-all">
                  {t('seeAll')} <Icon name="angle-right" />
                </Link>
              )}
            </header>
            <div className="ch-cheer-list">
              {recentCheers.map((cheer) => (
                <div className="ch-cheer-row" key={cheer.id}>
                  <img
                    src={cheer.photo_url || userFallback}
                    alt=""
                    className="ch-cheer-avatar"
                  />
                  <div className="ch-cheer-meta">
                    <p className="ch-cheer-name">{cheer.name}</p>
                    {cheer.message && (
                      <p className="ch-cheer-message">{cheer.message}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="party-horn" pack="sr" /></div>
            <div className="pp-empty-title">{t('noVotesYet')}</div>
            {votingActive && (
              <p className="pp-empty-sub">{t('publicVoteIntro')}</p>
            )}
          </div>
        )}
      </div>
    </Base>
  );
};

export default Page;
